/**
 * Offline Banner Component
 * Shows a subtle banner when user is offline
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './OfflineBanner.css';
import { trackEvent } from '../../utils/analytics';
import offlineQueue from '../../utils/offlineQueue';

const OfflineBanner = ({ onRetry }) => {
  const { t } = useTranslation('mobile', { useSuspense: false });
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [runtimeStatus, setRuntimeStatus] = useState(() => offlineQueue.getStatus());
  const hasReportedOfflineRef = useRef(false);
  const safeRuntimeStatus = runtimeStatus || {};
  const droppedCount = safeRuntimeStatus.lastInvalidation?.droppedCount || 0;

  useEffect(() => {
    const handleOnline = () => {
      console.log('[OfflineBanner] Network online');
      setIsOffline(false);
    };

    const handleOffline = () => {
      console.log('[OfflineBanner] Network offline');
      setIsOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    const syncRuntimeStatus = () => {
      setRuntimeStatus(offlineQueue.getStatus());
    };
    syncRuntimeStatus();
    const readyPromise = offlineQueue.waitUntilReady?.();
    if (readyPromise && typeof readyPromise.then === 'function') {
      readyPromise.then(syncRuntimeStatus);
    }

    const unsubscribe = typeof offlineQueue.subscribe === 'function'
      ? offlineQueue.subscribe(() => {
          syncRuntimeStatus();
        })
      : null;

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);

  useEffect(() => {
    if (isOffline) {
      if (!hasReportedOfflineRef.current) {
        trackEvent('offline_mode', {
          pending_actions: safeRuntimeStatus.queueSize || 0,
          offline_mutations_supported: safeRuntimeStatus.mutationsSupported,
          legacy_queue_dropped_count: droppedCount,
        });
        hasReportedOfflineRef.current = true;
      }
    } else {
      hasReportedOfflineRef.current = false;
    }
  }, [droppedCount, isOffline, safeRuntimeStatus.mutationsSupported, safeRuntimeStatus.queueSize]);

  if (!isOffline) {
    return null;
  }

  return (
    <div className="offline-banner">
      <div className="offline-banner-content">
        <div className="offline-banner-icon">📡</div>

        <div className="offline-banner-text">
          <span className="offline-banner-title">{t('pwa.offline.title', { defaultValue: 'Connection lost' })}</span>
          <span className="offline-banner-subtitle">
            {t('pwa.offline.readOnly', {
              defaultValue: 'Cached pages may still open, but edits, approvals and uploads require a live connection.'
            })}
          </span>
        </div>

        {onRetry && (
          <button
            className="offline-banner-btn"
            onClick={onRetry}
          >
            {t('common.actions.retry', { defaultValue: 'Retry' })}
          </button>
        )}
      </div>
    </div>
  );
};

export default OfflineBanner;
